// src/pages/TeacherMessageSettings.jsx
// 先生のメッセージ受付設定（/messages/settings）【2026-10-16】
//   ・受付のしかた（いつでも / 決めた時間だけ通知 / 受け付けない）と週の受付時間、代わりの窓口
//   ・臨時の停止（出張・休暇など）
//   ・定型文（返事によく使う文。50個まで）
//   API: GET/PUT /teacher/settings, POST/DELETE /teacher/pauses, GET/POST/PATCH/DELETE /teacher/templates
import React, { useCallback, useEffect, useState } from 'react';
import { msgApi } from '../messaging/api';
import { Page, Notice, Loading, Empty, CharCount, Tag } from '../messaging/admin/ui';
import { fmtDate, fmtDateTime, todayJst, errText, MAX_TEXT } from '../messaging/admin/util';
import { confirmDialog } from '../components/ConfirmDialog';

const DOW = ['日', '月', '火', '水', '木', '金', '土'];
const DOW_ORDER = [1, 2, 3, 4, 5, 6, 0]; // 月曜はじまりで並べる
const MAX_TEMPLATES = 50;

const MODES = [
  {
    key: 'open', title: 'いつでも受け付ける',
    desc: '届いたらすぐに通知します。',
  },
  {
    key: 'hours', title: '受付時間を決める',
    desc: '時間外に届いたメッセージも受け取りますが、受付時間になるまで通知しません。学生には「いまは受付時間外です」と表示されます。',
  },
  {
    key: 'closed', title: '受け付けない',
    desc: '学生は新しくメッセージを送れません。下の「代わりの窓口」を学生に案内します。',
  },
];

const STATE_TEXT = {
  open: 'いま受け付けています',
  off_hours: 'いまは受付時間外です',
  paused: '臨時で止めています',
  closed: '受け付けていません',
};

function stateDetail(now) {
  if (!now) return '';
  if (now.state === 'off_hours') {
    return now.next_open_at
      ? `届いたメッセージは受け取ります。通知は ${fmtDateTime(now.next_open_at)} からです。`
      : '届いたメッセージは受け取ります。通知は次の受付時間からです。';
  }
  if (now.state === 'paused') {
    const p = now.pause;
    return p ? `${fmtDate(p.ends_on)} まで止めています${p.note ? `（${p.note}）` : ''}。` : '';
  }
  if (now.state === 'closed') return '学生は新しくメッセージを送れません。';
  return '届いたメッセージはすぐ通知されます。';
}

// ── 受付設定 ───────────────────────────────────────────────
function AcceptanceCard({ data, onSaved }) {
  const [mode, setMode] = useState(data.settings.accept_mode || 'open');
  const [hours, setHours] = useState(() => (data.settings.weekly_hours || []).map(h => ({ ...h })));
  const [fallback, setFallback] = useState(data.settings.fallback_text || '');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  // 保存した値が変わったときだけ入れ直す（臨時の停止を足しただけで、書きかけが消えないように）
  const savedKey = JSON.stringify(data.settings);
  useEffect(() => {
    const s = JSON.parse(savedKey);
    setMode(s.accept_mode || 'open');
    setHours((s.weekly_hours || []).map(h => ({ ...h })));
    setFallback(s.fallback_text || '');
  }, [savedKey]);

  const addSlot = (dow) => {
    const last = hours.filter(h => h.dow === dow).slice(-1)[0];
    const start = last ? last.end : '09:00';
    const sh = Math.min(23, parseInt(start.slice(0, 2), 10) + 1);
    const end = last ? `${String(sh).padStart(2, '0')}:${start.slice(3, 5)}` : '17:00';
    setHours([...hours, { dow, start, end }]);
  };
  const updateSlot = (idx, key, value) => {
    setHours(hours.map((h, i) => (i === idx ? { ...h, [key]: value } : h)));
  };
  const removeSlot = (idx) => setHours(hours.filter((_, i) => i !== idx));
  const fillWeekdays = () => {
    setHours([1, 2, 3, 4, 5].map(d => ({ dow: d, start: '09:00', end: '17:00' })));
  };

  const save = async () => {
    setMsg(null);
    if (mode === 'hours' && hours.length === 0) {
      setMsg({ kind: 'error', text: '受付時間を1つ以上入れてください' });
      return;
    }
    const bad = hours.find(h => !h.start || !h.end || h.end <= h.start);
    if (bad) {
      setMsg({ kind: 'error', text: `${DOW[bad.dow]}曜日の時間を確かめてください（終わりは始まりより後に）` });
      return;
    }
    if (fallback.length > 300) {
      setMsg({ kind: 'error', text: '代わりの窓口は300文字までです' });
      return;
    }
    setSaving(true);
    const r = await msgApi.put('/teacher/settings', {
      accept_mode: mode,
      weekly_hours: hours.map(h => ({ dow: h.dow, start: h.start, end: h.end })),
      fallback_text: fallback.trim(),
    });
    setSaving(false);
    if (!r.success) { setMsg({ kind: 'error', text: errText(r) }); return; }
    setMsg({ kind: 'ok', text: '保存しました' });
    onSaved(r);
  };

  const now = data.now;
  return (
    <div className="card">
      <div className="card-header"><h2 style={{ margin: 0 }}>受付設定</h2></div>

      {now && (
        <div className={'ma-state ma-state--' + now.state}>
          <div>
            <div className="ma-state-t">{STATE_TEXT[now.state] || now.state}</div>
            <div className="ma-state-d">{stateDetail(now)}</div>
          </div>
        </div>
      )}

      <div className="ma-choices" role="radiogroup" aria-label="受付のしかた">
        {MODES.map(m => (
          <label key={m.key} className={'ma-choice' + (mode === m.key ? ' ma-choice--on' : '')}>
            <input type="radio" name="accept_mode" value={m.key} checked={mode === m.key} onChange={() => setMode(m.key)} />
            <div>
              <div className="ma-choice-t">{m.title}</div>
              <div className="ma-choice-d">{m.desc}</div>
            </div>
          </label>
        ))}
      </div>

      {mode === 'hours' && (
        <div className="ma-field">
          <div className="ma-field-label">
            <span>週の受付時間</span>
            {hours.length === 0 && (
              <button type="button" className="btn-link" onClick={fillWeekdays}>月〜金 9:00〜17:00 を入れる</button>
            )}
          </div>
          <div className="ma-week">
            {DOW_ORDER.map(dow => {
              const slots = hours.map((h, i) => ({ ...h, i })).filter(h => h.dow === dow);
              const cls = 'ma-day-name' + (dow === 0 ? ' ma-day-name--sun' : dow === 6 ? ' ma-day-name--sat' : '');
              return (
                <div className="ma-day" key={dow}>
                  <div className={cls}>{DOW[dow]}</div>
                  <div className="ma-day-slots">
                    {slots.length === 0 && <span className="ma-slot-none">受け付けない</span>}
                    {slots.map(s => (
                      <div className="ma-slot" key={s.i}>
                        <input type="time" className="ma-input ma-input--time" value={s.start}
                          onChange={e => updateSlot(s.i, 'start', e.target.value)} aria-label={`${DOW[dow]}曜日の始まり`} />
                        <span>〜</span>
                        <input type="time" className="ma-input ma-input--time" value={s.end}
                          onChange={e => updateSlot(s.i, 'end', e.target.value)} aria-label={`${DOW[dow]}曜日の終わり`} />
                        <button type="button" className="ma-x" onClick={() => removeSlot(s.i)} aria-label="この時間を消す">×</button>
                      </div>
                    ))}
                    <div>
                      <button type="button" className="btn-link" onClick={() => addSlot(dow)} disabled={hours.length >= 28}>
                        ＋ 時間を足す
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="ma-hint">同じ曜日に、昼休みをはさんで2つの時間を入れることもできます（全部で28個まで）。</div>
        </div>
      )}

      <div className="ma-field">
        <div className="ma-field-label">
          <span>代わりの窓口（任意）</span>
          <CharCount value={fallback} max={300} />
        </div>
        <textarea className="ma-textarea" style={{ minHeight: '4.5rem' }} value={fallback}
          onChange={e => setFallback(e.target.value)}
          placeholder="例）急ぎの用件は教務課（内線1234）へ連絡してください。" />
        <div className="ma-hint">受付時間外・受け付けないときに、学生の画面に出します。</div>
      </div>

      {msg && <Notice kind={msg.kind} onClose={() => setMsg(null)}>{msg.text}</Notice>}
      <div className="ma-actions ma-actions--end">
        <button type="button" className="btn-primary" onClick={save} disabled={saving}>
          {saving ? '保存中...' : '保存する'}
        </button>
      </div>
    </div>
  );
}

// ── 臨時の停止 ─────────────────────────────────────────────
function PausesCard({ pauses, onChanged }) {
  const [start, setStart] = useState(todayJst());
  const [end, setEnd] = useState(todayJst());
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const add = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (!start || !end) { setMsg({ kind: 'error', text: '始まりと終わりの日を入れてください' }); return; }
    if (end < start) { setMsg({ kind: 'error', text: '終わりの日は始まりの日以降にしてください' }); return; }
    setBusy(true);
    const r = await msgApi.post('/teacher/pauses', { starts_on: start, ends_on: end, note: note.trim() });
    setBusy(false);
    if (!r.success) { setMsg({ kind: 'error', text: errText(r) }); return; }
    setNote('');
    setMsg({ kind: 'ok', text: '臨時の停止を入れました' });
    onChanged();
  };

  const remove = async (p) => {
    if (!(await confirmDialog(`${fmtDate(p.starts_on)}〜${fmtDate(p.ends_on)} の停止を消しますか？`))) return;
    setMsg(null);
    const r = await msgApi.del('/teacher/pauses/' + p.id);
    if (!r.success) { setMsg({ kind: 'error', text: errText(r) }); return; }
    onChanged();
  };

  return (
    <div className="card">
      <div className="card-header">
        <h2 style={{ margin: 0 }}>臨時の停止</h2>
        <div className="ma-muted" style={{ marginTop: '0.25rem', fontSize: '0.82rem' }}>
          出張や休暇のあいだ、メッセージの受付を止めます。上の受付設定より優先されます。
        </div>
      </div>

      {pauses.length === 0 ? (
        <Empty>いま予定している停止はありません。</Empty>
      ) : (
        <div className="ma-list">
          {pauses.map(p => (
            <div className="ma-item" key={p.id}>
              <div className="ma-actions" style={{ justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: 700 }}>{fmtDate(p.starts_on)} 〜 {fmtDate(p.ends_on)}</div>
                  {p.note && <div className="ma-muted" style={{ marginTop: '0.2rem' }}>{p.note}</div>}
                </div>
                <button type="button" className="btn-danger ma-btn-sm" onClick={() => remove(p)}>消す</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={add} style={{ marginTop: '1rem' }}>
        <div className="ma-actions" style={{ alignItems: 'flex-end' }}>
          <div>
            <div className="ma-field-label"><span>始まり</span></div>
            <input type="date" className="ma-input" value={start} min={todayJst()} onChange={e => setStart(e.target.value)} />
          </div>
          <div>
            <div className="ma-field-label"><span>終わり</span></div>
            <input type="date" className="ma-input" value={end} min={start || todayJst()} onChange={e => setEnd(e.target.value)} />
          </div>
          <div style={{ flex: '1 1 200px' }}>
            <div className="ma-field-label"><span>メモ（任意・学生にも見えます）</span><CharCount value={note} max={200} /></div>
            <input type="text" className="ma-input ma-input--full" value={note} maxLength={200}
              onChange={e => setNote(e.target.value)} placeholder="例）学会出張のため" />
          </div>
          <button type="submit" className="btn-primary" disabled={busy}>{busy ? '追加中...' : '停止を入れる'}</button>
        </div>
      </form>
      {msg && <div style={{ marginTop: '0.75rem' }}><Notice kind={msg.kind} onClose={() => setMsg(null)}>{msg.text}</Notice></div>}
    </div>
  );
}

// ── 定型文 ─────────────────────────────────────────────────
function TemplateEditor({ initial, onSubmit, onCancel, submitLabel }) {
  const [title, setTitle] = useState(initial ? initial.title : '');
  const [text, setText] = useState(initial ? initial.text : '');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await onSubmit({ title: title.trim(), text: text.trim() });
    setBusy(false);
    if (ok && !initial) { setTitle(''); setText(''); }
  };
  const invalid = !title.trim() || !text.trim() || title.length > 60 || text.length > MAX_TEXT;
  return (
    <form onSubmit={submit}>
      <div className="ma-field">
        <div className="ma-field-label"><span>名前</span><CharCount value={title} max={60} /></div>
        <input type="text" className="ma-input ma-input--full" value={title} onChange={e => setTitle(e.target.value)}
          placeholder="例）課題の提出期限について" />
      </div>
      <div className="ma-field">
        <div className="ma-field-label"><span>本文</span><CharCount value={text} max={MAX_TEXT} /></div>
        <textarea className="ma-textarea" value={text} onChange={e => setText(e.target.value)} />
      </div>
      <div className="ma-actions ma-actions--end">
        {onCancel && <button type="button" className="btn-secondary" onClick={onCancel}>やめる</button>}
        <button type="submit" className="btn-primary" disabled={busy || invalid}>{busy ? '保存中...' : submitLabel}</button>
      </div>
    </form>
  );
}

function TemplatesCard() {
  const [list, setList] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState(null);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    const r = await msgApi.get('/teacher/templates');
    if (!r.success) { setErr(errText(r)); setList([]); return; }
    setErr('');
    setList(r.templates || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (body) => {
    setMsg(null);
    const r = await msgApi.post('/teacher/templates', body);
    if (!r.success) { setMsg({ kind: 'error', text: errText(r) }); return false; }
    setAdding(false);
    setMsg({ kind: 'ok', text: '定型文を追加しました' });
    load();
    return true;
  };
  const update = async (id, body) => {
    setMsg(null);
    const r = await msgApi.patch('/teacher/templates/' + id, body);
    if (!r.success) { setMsg({ kind: 'error', text: errText(r) }); return false; }
    setEditing(null);
    load();
    return true;
  };
  const remove = async (t) => {
    if (!(await confirmDialog(`定型文「${t.title}」を消しますか？`))) return;
    setMsg(null);
    const r = await msgApi.del('/teacher/templates/' + t.id);
    if (!r.success) { setMsg({ kind: 'error', text: errText(r) }); return; }
    load();
  };

  const full = list && list.length >= MAX_TEMPLATES;
  return (
    <div className="card">
      <div className="card-header">
        <h2 style={{ margin: 0 }}>定型文</h2>
        <div className="ma-muted" style={{ marginTop: '0.25rem', fontSize: '0.82rem' }}>
          学生への返事によく使う文を登録しておくと、メッセージ画面からすぐに入れられます。
          {list && `（${list.length} / ${MAX_TEMPLATES}個）`}
        </div>
      </div>
      {err && <Notice kind="error">{err}</Notice>}
      {msg && <Notice kind={msg.kind} onClose={() => setMsg(null)}>{msg.text}</Notice>}
      {!list ? <Loading /> : list.length === 0 ? (
        <Empty>まだ定型文はありません。</Empty>
      ) : (
        <div className="ma-list">
          {list.map(t => (
            <div className="ma-item" key={t.id}>
              {editing === t.id ? (
                <TemplateEditor initial={t} submitLabel="保存する"
                  onSubmit={(b) => update(t.id, b)} onCancel={() => setEditing(null)} />
              ) : (
                <>
                  <div className="ma-item-title">{t.title}</div>
                  <div className="ma-item-text">{t.text}</div>
                  <div className="ma-actions ma-actions--end ma-actions--top">
                    <button type="button" className="btn-secondary ma-btn-sm" onClick={() => setEditing(t.id)}>直す</button>
                    <button type="button" className="btn-danger ma-btn-sm" onClick={() => remove(t)}>消す</button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
      <div style={{ marginTop: '1rem' }}>
        {adding ? (
          <div className="ma-item">
            <TemplateEditor submitLabel="追加する" onSubmit={create} onCancel={() => setAdding(false)} />
          </div>
        ) : (
          <div className="ma-actions">
            <button type="button" className="btn-primary" onClick={() => setAdding(true)} disabled={!list || full}>
              ＋ 定型文を追加
            </button>
            {full && <Tag tone="amber">{MAX_TEMPLATES}個までです。使わないものを消してください</Tag>}
          </div>
        )}
      </div>
    </div>
  );
}

export default function TeacherMessageSettings({ user }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const r = await msgApi.get('/teacher/settings');
    setLoading(false);
    if (!r.success) { setErr(errText(r)); return; }
    setErr('');
    setData(r);
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <Page title="メッセージの受付設定" lead="学生からの個別メッセージを、いつ受け付けて、いつ通知するかを決めます。">
      {loading ? <div className="card"><Loading /></div>
        : err ? (
          <div className="card">
            <Notice kind="error">{err}</Notice>
            <button type="button" className="btn-secondary" onClick={() => { setLoading(true); load(); }}>もう一度試す</button>
          </div>
        ) : data && (
          <>
            <AcceptanceCard data={data} onSaved={setData} />
            <PausesCard pauses={data.pauses || []} onChanged={load} />
            <TemplatesCard />
          </>
        )}
    </Page>
  );
}
